import React, { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Input,
    FormGroup,
    Label
} from 'reactstrap';
import classnames from 'classnames';

interface CodeExamplesModalProps {
    isOpen: boolean;
    toggle: () => void;
    endpoint: {
        title: string;
        endpointSlug: string;
        sourceType?: number;
        envId?: number;
    } | null;
}

const CodeExamplesModal: React.FC<CodeExamplesModalProps> = ({ isOpen, toggle, endpoint }) => {
    const [activeTab, setActiveTab] = useState('curl');

    if (!endpoint) return null;

    const getBaseUrl = () => {
        // Both sandbox and production use the same API URL now
        return process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev';
    };

    const apiUrl = `${getBaseUrl()}/v1/flow/${endpoint.endpointSlug}`;
    const sourceType = endpoint.sourceType || 1;

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const getCurlExample = () => {
        if (sourceType === 1) {
            return `# Upload a file (multipart/form-data)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -F "file=@/path/to/your/file.pdf"

# Or send base64-encoded file (application/json)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_FILE_CONTENT"
  }'`;
        } else if (sourceType === 2) {
            return `# Send text/HTML content (base64-encoded)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_TEXT_CONTENT"
  }'

# Example: Encode text to base64
# echo "Your text content here" | base64`;
        } else if (sourceType === 3) {
            return `# Send image (base64-encoded)
curl -X POST "${apiUrl}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "file": "BASE64_ENCODED_IMAGE_CONTENT"
  }'

# Example: Encode image to base64
# base64 /path/to/your/image.png | tr -d '\\n'`;
        }
        return '';
    };

    const getPhpExample = () => {
        if (sourceType === 1) {
            return `<?php
// Upload a file
$curl = curl_init();

$file = new CURLFile('/path/to/your/file.pdf');

curl_setopt_array($curl, [
    CURLOPT_URL => "${apiUrl}",
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer YOUR_API_TOKEN'
    ],
    CURLOPT_POSTFIELDS => [
        'file' => $file
    ]
]);

$response = curl_exec($curl);
curl_close($curl);

echo $response;
?>`;
        } else {
            return `<?php
// Send base64-encoded content
$data = [
    'file' => base64_encode(file_get_contents('/path/to/your/file'))
];

$curl = curl_init();

curl_setopt_array($curl, [
    CURLOPT_URL => "${apiUrl}",
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer YOUR_API_TOKEN',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data)
]);

$response = curl_exec($curl);
curl_close($curl);

echo $response;
?>`;
        }
    };

    const getJavaExample = () => {
        if (sourceType === 1) {
            return `import java.io.*;
import java.net.*;
import java.nio.file.*;

public class ApiClient {
    public static void main(String[] args) throws Exception {
        String apiUrl = "${apiUrl}";
        String token = "YOUR_API_TOKEN";
        Path filePath = Paths.get("/path/to/your/file.pdf");

        // Read file as base64
        byte[] fileContent = Files.readAllBytes(filePath);
        String base64File = java.util.Base64.getEncoder().encodeToString(fileContent);

        // Create JSON payload
        String jsonPayload = "{\\"file\\": \\"" + base64File + "\\"}";

        // Send request
        HttpURLConnection conn = (HttpURLConnection) new URL(apiUrl).openConnection();
        conn.setRequestMethod("POST");
        conn.setRequestProperty("Authorization", "Bearer " + token);
        conn.setRequestProperty("Content-Type", "application/json");
        conn.setDoOutput(true);

        try (OutputStream os = conn.getOutputStream()) {
            os.write(jsonPayload.getBytes("UTF-8"));
        }

        // Read response
        int responseCode = conn.getResponseCode();
        BufferedReader in = new BufferedReader(
            new InputStreamReader(conn.getInputStream()));
        String inputLine;
        StringBuilder response = new StringBuilder();

        while ((inputLine = in.readLine()) != null) {
            response.append(inputLine);
        }
        in.close();

        System.out.println("Response: " + response.toString());
    }
}`;
        } else {
            return `import java.io.*;
import java.net.*;

public class ApiClient {
    public static void main(String[] args) throws Exception {
        String apiUrl = "${apiUrl}";
        String token = "YOUR_API_TOKEN";

        // Create JSON payload with base64 content
        String content = "Your content here";
        String base64Content = java.util.Base64.getEncoder()
            .encodeToString(content.getBytes("UTF-8"));
        String jsonPayload = "{\\"file\\": \\"" + base64Content + "\\"}";

        // Send request
        HttpURLConnection conn = (HttpURLConnection) new URL(apiUrl).openConnection();
        conn.setRequestMethod("POST");
        conn.setRequestProperty("Authorization", "Bearer " + token);
        conn.setRequestProperty("Content-Type", "application/json");
        conn.setDoOutput(true);

        try (OutputStream os = conn.getOutputStream()) {
            os.write(jsonPayload.getBytes("UTF-8"));
        }

        // Read response
        BufferedReader in = new BufferedReader(
            new InputStreamReader(conn.getInputStream()));
        String inputLine;
        StringBuilder response = new StringBuilder();

        while ((inputLine = in.readLine()) != null) {
            response.append(inputLine);
        }
        in.close();

        System.out.println("Response: " + response.toString());
    }
}`;
        }
    };

    const getPythonExample = () => {
        if (sourceType === 1) {
            return `import requests
import base64

# API configuration
api_url = "${apiUrl}"
token = "YOUR_API_TOKEN"

# Read and encode file
with open('/path/to/your/file.pdf', 'rb') as file:
    file_content = file.read()
    base64_file = base64.b64encode(file_content).decode('utf-8')

# Send request
headers = {
    'Authorization': f'Bearer {token}',
    'Content-Type': 'application/json'
}

payload = {
    'file': base64_file
}

response = requests.post(api_url, json=payload, headers=headers)

print(f"Status Code: {response.status_code}")
print(f"Response: {response.json()}")`;
        } else {
            return `import requests
import base64

# API configuration
api_url = "${apiUrl}"
token = "YOUR_API_TOKEN"

# Encode content to base64
content = "Your content here"
base64_content = base64.b64encode(content.encode('utf-8')).decode('utf-8')

# Send request
headers = {
    'Authorization': f'Bearer {token}',
    'Content-Type': 'application/json'
}

payload = {
    'file': base64_content
}

response = requests.post(api_url, json=payload, headers=headers)

print(f"Status Code: {response.status_code}")
print(f"Response: {response.json()}")`;
        }
    };

    const getTypeScriptExample = () => {
        if (sourceType === 1) {
            return `import axios from 'axios';
import * as fs from 'fs';

// API configuration
const apiUrl = '${apiUrl}';
const token = 'YOUR_API_TOKEN';

// Read and encode file
const fileContent = fs.readFileSync('/path/to/your/file.pdf');
const base64File = fileContent.toString('base64');

// Send request
async function callApi() {
    try {
        const response = await axios.post(
            apiUrl,
            {
                file: base64File
            },
            {
                headers: {
                    'Authorization': \`Bearer \${token}\`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Status:', response.status);
        console.log('Response:', response.data);
    } catch (error) {
        console.error('Error:', error);
    }
}

callApi();`;
        } else {
            return `import axios from 'axios';

// API configuration
const apiUrl = '${apiUrl}';
const token = 'YOUR_API_TOKEN';

// Encode content to base64
const content = 'Your content here';
const base64Content = Buffer.from(content).toString('base64');

// Send request
async function callApi() {
    try {
        const response = await axios.post(
            apiUrl,
            {
                file: base64Content
            },
            {
                headers: {
                    'Authorization': \`Bearer \${token}\`,
                    'Content-Type': 'application/json'
                }
            }
        );

        console.log('Status:', response.status);
        console.log('Response:', response.data);
    } catch (error) {
        console.error('Error:', error);
    }
}

callApi();`;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
            <ModalHeader toggle={toggle}>
                API Integration Examples
            </ModalHeader>

            <ModalBody>
                <div className="mb-3">
                    <FormGroup>
                        <Label className="fw-semibold">Endpoint Title</Label>
                        <Input
                            type="text"
                            value={endpoint.title}
                            readOnly
                            style={{ backgroundColor: '#e9ecef' }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label className="fw-semibold">API Endpoint URL</Label>
                        <Input
                            type="text"
                            value={apiUrl}
                            readOnly
                            style={{ backgroundColor: '#e9ecef', fontFamily: 'monospace' }}
                        />
                    </FormGroup>
                </div>

                <Nav tabs>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === 'curl' })}
                            onClick={() => toggleTab('curl')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="ri-terminal-line me-1"></i>
                            cURL
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === 'php' })}
                            onClick={() => toggleTab('php')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="ri-code-line me-1"></i>
                            PHP
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === 'java' })}
                            onClick={() => toggleTab('java')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="ri-braces-line me-1"></i>
                            Java
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === 'python' })}
                            onClick={() => toggleTab('python')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="ri-code-s-slash-line me-1"></i>
                            Python
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === 'typescript' })}
                            onClick={() => toggleTab('typescript')}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="ri-file-code-line me-1"></i>
                            TypeScript
                        </NavLink>
                    </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="mt-3">
                    <TabPane tabId="curl">
                        <Input
                            type="textarea"
                            rows="15"
                            value={getCurlExample()}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f8f9fa' }}
                        />
                    </TabPane>
                    <TabPane tabId="php">
                        <Input
                            type="textarea"
                            rows="15"
                            value={getPhpExample()}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f8f9fa' }}
                        />
                    </TabPane>
                    <TabPane tabId="java">
                        <Input
                            type="textarea"
                            rows="15"
                            value={getJavaExample()}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f8f9fa' }}
                        />
                    </TabPane>
                    <TabPane tabId="python">
                        <Input
                            type="textarea"
                            rows="15"
                            value={getPythonExample()}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f8f9fa' }}
                        />
                    </TabPane>
                    <TabPane tabId="typescript">
                        <Input
                            type="textarea"
                            rows="15"
                            value={getTypeScriptExample()}
                            readOnly
                            style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f8f9fa' }}
                        />
                    </TabPane>
                </TabContent>
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CodeExamplesModal;
